import React, { useContext, useEffect, useState } from "react";
import { collection, getDocs, query, orderBy, where, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { UserContext } from "../../contexts/user.context";
import { updateProperty } from "../../api/wordpress";
import { toast, Toaster } from "sonner";
import { AiOutlineLoading3Quarters, AiOutlineArrowLeft } from "react-icons/ai";

const AdminEscrowPayouts = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [selectedTx, setSelectedTx] = useState(null);
  const [ownerBankDetails, setOwnerBankDetails] = useState(null);
  const [strikes, setStrikes] = useState({ ruumie: 0, owner: 0 });
  const [ownerDebt, setOwnerDebt] = useState(0);
  
  const [actionLoading, setActionLoading] = useState(false);
  const [adminPin, setAdminPin] = useState("");
  const [faultParty, setFaultParty] = useState("");
  const [newStatus, setNewStatus] = useState("");

  const { fsUser } = useContext(UserContext);
  const accessToken = fsUser?.accessToken;
  const token = JSON.parse(localStorage.getItem("user"))?.token;

    document.title = "Payouts";

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "paymentEntries"),
        where("escrowStatus", "in", ["Held", "Move_In_Confirmed", "Disputed", "Processing_Transfer", "Refunded"]),
        orderBy("createdAt", "desc")
      );

      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setTransactions(data);
    } catch (error) {
      console.error("Error fetching escrows:", error);
      toast.error("Failed to load escrow queue.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  const handleViewDetails = async (tx) => {
    setSelectedTx(tx);
    setAdminPin("");
    setFaultParty("");
    setNewStatus("");
    setOwnerBankDetails(null);
    setStrikes({ ruumie: 0, owner: 0 });
    setOwnerDebt(0);

    try {
      if (tx.receiverID) {
        const subDoc = await getDoc(doc(db, "subaccounts", tx.receiverID));
        if (subDoc.exists()) setOwnerBankDetails(subDoc.data());
      }

      let ruumieCount = 0;
      let ownerCount = 0;
      let ownerDebtAmount = 0;

      if (tx.userId) {
        const ruumieDoc = await getDoc(doc(db, "users", tx.userId));
        if (ruumieDoc.exists()) ruumieCount = ruumieDoc.data().cancellationStrikes || 0;
      }

      if (tx.receiverID) {
        const ownerDoc = await getDoc(doc(db, "users", tx.receiverID));
        if (ownerDoc.exists()) {
          ownerCount = ownerDoc.data().cancellationStrikes || 0;
          ownerDebtAmount = ownerDoc.data().totalOutstandingDebt || 0;
        }
      }

      setStrikes({ ruumie: ruumieCount, owner: ownerCount });
      setOwnerDebt(ownerDebtAmount);
    } catch (err) {
      console.error("Failed to fetch external details:", err);
    }
  };

  const confirmPayout = async () => {
    if (adminPin !== "Ruumies123") {
      toast.error("Incorrect Admin PIN.");
      return;
    }

    setActionLoading(true);
    try {
      const payload = { transactionDocId: selectedTx.reference };
      
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/payments/approve-payout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Payout initiation failed.");

      // Display detailed payout message from backend
      const successMessage = result.message || "Payout processed successfully.";
      
      // Check if debt was deducted
      if (successMessage.includes("No transfer")) {
        toast.warning(successMessage);
      } else {
        toast.success(successMessage);
      }
      
      setSelectedTx(null);
      fetchTransactions();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const confirmRefund = async () => {
    if (adminPin !== "Ruumies123") {
      toast.error("Incorrect Admin PIN.");
      return;
    }

    if (!faultParty) {
      toast.error("You must select who is at fault.");
      return;
    }

    setActionLoading(true);
    try {
      const payload = { 
        transactionDocId: selectedTx.reference,
        faultParty: faultParty 
      }; 
      const response = await fetch(`${process.env.REACT_APP_BASE_URL || "https://ruumies-vfd-nodejs-engine-juft.vercel.app"}/api/payments/refund`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.message || "Refund failed.");

      toast.success("Refund processed and property reopened.");
      setSelectedTx(null);
      fetchTransactions();
    } catch (error) {
      toast.error(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const changeStatus = async () => {
    if (!newStatus) {
      toast.error("Please select a status.");
      return;
    }

    setActionLoading(true);
    try {
      // Update Firebase
      await updateDoc(doc(db, "paymentEntries", selectedTx.reference), {
        escrowStatus: newStatus
      });

      // Convert Firebase status to WordPress status
      const wpStatus = newStatus === "Move_In_Confirmed" ? "payout_ready" 
                     : newStatus === "Disputed" ? "disputed"
                     : "in_escrow"; // For "Held"

      // Update WordPress property
      const formData = new FormData();
      formData.append("id", selectedTx.metadata?.propertyId);
      formData.append("escrow_status", wpStatus);
      
      await updateProperty(token, formData);

      toast.success(`Status changed to ${newStatus.replace(/_/g, " ")}`);
      setSelectedTx(null);
      setNewStatus("");
      fetchTransactions();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(`Error: ${error.message}`);
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      Held: "bg-yellow-50 text-yellow-700 border-yellow-200",
      Move_In_Confirmed: "bg-green-50 text-green-700 border-green-200",
      Disputed: "bg-red-50 text-red-700 border-red-200",
      Processing_Transfer: "bg-blue-50 text-blue-700 border-blue-200"
    };
    const displayStatus = status?.replace(/_/g, " ");
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${styles[status] || "bg-gray-100 text-gray-800"}`}>
        {displayStatus}
      </span>
    );
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-pulse flex flex-col items-center">
        <div className="h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-2"></div>
      </div>
    </div>
  );

  if (selectedTx) {
    const isReadyToPay = selectedTx.escrowStatus === "Move_In_Confirmed";
    const rentAmount = Number(selectedTx.rentAmount || selectedTx.metadata?.rentAmount || 0);
    
    return (
      <div className="min-h-screen bg-gray-50 p-1">
        <Toaster position="top-center" richColors />
        <div className="mx-auto">
          
          <button onClick={() => setSelectedTx(null)} className="flex items-center text-sm text-gray-500 hover:text-gray-900 mb-6 transition-colors">
            <AiOutlineArrowLeft className="mr-2" /> Back
          </button>

          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Transaction Details</h2>
              <p className="text-gray-500 text-xs mt-1">ID: {selectedTx.reference}</p>
            </div>
            {getStatusBadge(selectedTx.escrowStatus)}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">Payment Summary</h3>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Total Paid</p>
                    <p className="text-lg text-gray-900">₦{Number(selectedTx.amount).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1">Rent Amount</p>
                    <p className="text-lg text-gray-900">₦{rentAmount.toLocaleString()}</p>
                  </div>
                </div>
                
                {/* Debt Deduction Display */}
                {ownerDebt > 0 && (
                  <div className="bg-orange-50/50 p-4 rounded-lg border border-orange-200 mb-4">
                    <p className="text-[11px] font-semibold text-orange-700 uppercase tracking-wider mb-3">Debt Calculation</p>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Pending Payout:</span>
                        <span className="font-medium text-gray-900">₦{Number(selectedTx.ownerPendingAmount).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Outstanding Debt:</span>
                        <span className="font-medium text-orange-700">-₦{ownerDebt.toLocaleString()}</span>
                      </div>
                      <div className="border-t border-orange-200 pt-2 flex justify-between items-center">
                        <span className="font-semibold text-gray-900">Amount to Transfer:</span>
                        <span className="text-lg font-bold text-orange-900">
                          ₦{Math.max(0, Number(selectedTx.ownerPendingAmount) - ownerDebt).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="bg-blue-50/50 p-4 rounded-lg border border-blue-100">
                  <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-wider mb-1">Owner Net Payout</p>
                  <p className="text-2xl font-bold text-blue-900">₦{Number(selectedTx.ownerPendingAmount).toLocaleString()}</p>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">User Identity</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Tenant</p>
                     <p className="text-sm font-medium text-gray-900">{selectedTx.customer?.first_name || "N/A"} </p>
                     <p className="text-sm font-medium text-gray-900">{selectedTx.customer?.email || "N/A"} </p>


                    <div className="flex items-center gap-2 mb-1">
                      {strikes.ruumie > 0 && (
                        <span className="bg-red-50 text-red-600 border border-red-200 text-[10px] px-2 py-0.5 rounded-full font-medium">
                          {strikes.ruumie} Strikes
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Owner</p>
                    <div className="flex items-center gap-2 mb-1">
                     <p className="text-sm font-medium text-gray-900">{selectedTx.metadata?.first_name || "N/A"} {selectedTx.metadata?.last_name || ""}</p>

                      {strikes.owner > 0 && (
                        <span className="bg-red-50 text-red-600 border border-red-200 text-[10px] px-2 py-0.5 rounded-full font-medium">
                          {strikes.owner} Strikes
                        </span>
                      )}
                    </div>
                  <p className="text-sm text-gray-500">{selectedTx.metadata?.email}</p>
                    <p className="text-sm text-gray-500">{selectedTx.metadata?.phone_number || "N/A"}</p>

                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">Property Information</h3>
                <div>
                  <p className="font-medium text-gray-900 mb-1">{selectedTx.metadata?.title}</p>
                  <p className="text-sm text-gray-500">ID: {selectedTx.metadata?.propertyId} • Type: {selectedTx.metadata?.type}</p>
                </div>
              </div>

            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">Owner Bank Information</h3>
                {ownerBankDetails ? (
                  <div>
                    <p className="text-sm font-medium text-gray-900">{ownerBankDetails.account_name}</p>
                    <p className="text-sm text-gray-500 mt-1">{ownerBankDetails.subaccount?.settlement_bank || ownerBankDetails.bank_code} • {ownerBankDetails.account_number}</p>
                    
                    {/* Outstanding Debt Display */}
                    {ownerDebt > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Outstanding Debt:</span>
                          <span className="text-sm font-bold text-red-600">₦{ownerDebt.toLocaleString()}</span>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-2">Will be deducted from payout</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Loading bank details...</p>
                )}
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">Admin Actions</h3>
                
                <input
                  type="password"
                  placeholder="Enter Admin PIN"
                  value={adminPin}
                  onChange={(e) => setAdminPin(e.target.value)}
                  disabled={actionLoading}
                  className="w-full text-sm p-2.5 border border-gray-300 rounded-lg mb-6 focus:ring-1 focus:ring-blue-500 focus:outline-none disabled:bg-gray-50"
                />

                <div className="mb-6">
                  <p className="text-xs text-gray-500 mb-2">Process Payout</p>
                  {ownerDebt > 0 && (
                    <p className="text-[10px] text-orange-600 bg-orange-50 p-2 rounded mb-3 border border-orange-200">
                      ⚠️ Owner has outstanding debt of ₦{ownerDebt.toLocaleString()} that will be deducted.
                    </p>
                  )}
                  <button
                    onClick={confirmPayout}
                    disabled={!isReadyToPay || !adminPin || actionLoading}
                    className="w-full flex justify-center items-center py-2.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
                  >
                    {actionLoading && isReadyToPay ? <AiOutlineLoading3Quarters className="animate-spin" /> : "Approve Payout"}
                  </button>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1">Cancel & Refund</p>
                  <p className="text-[10px] text-gray-400 mb-3">Current Strikes — Tenant: {strikes.ruumie} | Owner: {strikes.owner}</p>
                  <select
                    value={faultParty}
                    onChange={(e) => setFaultParty(e.target.value)}
                    disabled={actionLoading}
                    className="w-full text-sm p-2.5 border border-gray-300 rounded-lg mb-3 bg-white focus:ring-1 focus:ring-red-500 focus:outline-none disabled:bg-gray-50"
                  >
                    <option value="">-- Assign Fault --</option>
                    <option value="ruumie">Tenant Flaked</option>
                    <option value="owner">Owner Defaulted</option>
                    <option value="mutual">Mutual Agreement</option>
                  </select>

                  {faultParty && (
                    <div className="mb-3 p-3 bg-gray-50 border border-gray-200 rounded-lg text-xs space-y-2">
                      {faultParty === "owner" && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Tenant Refund (100% + 1%):</span>
                            <span className="font-medium text-red-600">₦{(rentAmount * 1.01).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-2 mt-1">
                            <span className="text-gray-500">Owner Penalty (-1%):</span>
                            <span className="font-medium text-red-600">-₦{(rentAmount * 0.01).toLocaleString()}</span>
                          </div>
                        </>
                      )}
                      {faultParty === "mutual" && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Tenant Refund (100%):</span>
                            <span className="font-medium text-gray-900">₦{rentAmount.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-2 mt-1">
                            <span className="text-gray-500">Owner Penalty (-1%):</span>
                            <span className="font-medium text-red-600">-₦{(rentAmount * 0.01).toLocaleString()}</span>
                          </div>
                        </>
                      )}
                      {faultParty === "ruumie" && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Tenant Refund (95%):</span>
                            <span className="font-medium text-red-600">₦{(rentAmount * 0.95).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">Owner Comp (4%):</span>
                            <span className="font-medium text-green-600">₦{(rentAmount * 0.04).toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-2 mt-1">
                            <span className="text-gray-500">Platform Fee (1%):</span>
                            <span className="font-medium text-gray-900">₦{(rentAmount * 0.01).toLocaleString()}</span>
                          </div>
                        </>
                      )}
                    </div>
                  )}

                  <button
                    onClick={confirmRefund}
                    disabled={!faultParty || !adminPin || actionLoading}
                    className="w-full flex justify-center items-center py-2.5 bg-white border border-red-200 text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 disabled:bg-gray-50 disabled:text-gray-400 disabled:border-gray-200 transition-colors"
                  >
                    {actionLoading && faultParty ? <AiOutlineLoading3Quarters className="animate-spin" /> : "Process Refund"}
                  </button>
                </div>

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-3">Change Escrow Status</p>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    disabled={actionLoading}
                    className="w-full text-sm p-2.5 border border-gray-300 rounded-lg mb-3 bg-white focus:ring-1 focus:ring-purple-500 focus:outline-none disabled:bg-gray-50"
                  >
                    <option value="">-- Select New Status --</option>
                    <option value="Held">Held</option>
                    <option value="Move_In_Confirmed">Move In Confirmed</option>
                    <option value="Disputed">Disputed</option>
                  </select>

                  <button
                    onClick={changeStatus}
                    disabled={!newStatus || actionLoading}
                    className="w-full flex justify-center items-center py-2.5 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-400 transition-colors"
                  >
                    {actionLoading && newStatus ? <AiOutlineLoading3Quarters className="animate-spin" /> : "Update Status"}
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-1">
      <Toaster position="top-center" richColors />
      <div className=" mx-auto">
        
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Escrow Management</h2>
          <div className="bg-white px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-600">
            Pending Actions: <span className="text-blue-600 ml-1">{transactions.filter(t => t.escrowStatus === "Move_In_Confirmed").length}</span>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead>
                <tr className="bg-gray-50/50 border-b border-gray-200 text-[11px] uppercase tracking-wider text-gray-500 font-semibold">
                  <th className="px-6 py-4">Property</th>
                  <th className="px-6 py-4">Users</th>
                  <th className="px-6 py-4">Payout</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map(tx => (
                  <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900 max-w-[200px] truncate">{tx.metadata?.title}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{tx.metadata?.propertyId}</p>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900 text-xs">{tx.metadata?.email}</p>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">
                      ₦{Number(tx.ownerPendingAmount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(tx.escrowStatus)}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleViewDetails(tx)}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Review
                      </button>
                    </td>
                  </tr>
                ))}
                {transactions.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      No active escrows found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminEscrowPayouts;